/**
 * Hooks du centre de notifications (docs/14 §8).
 *
 * ⚠️ **Le texte des notifications est rendu ici, pas par le serveur.** La table
 * `notifications.notifications` ne porte qu'un `type` et un `payload` ; c'est
 * i18next qui en fait une phrase. Deux raisons : la règle 10 de `CLAUDE.md`
 * (aucune chaîne visible en dur côté frontend), et surtout le fait qu'un texte
 * figé à l'écriture resterait en français dans une interface passée à l'anglais
 * — une notification se relit des semaines plus tard.
 *
 * Les emails, eux, sont rendus côté serveur : ils partent une fois, dans la
 * langue du compte à cet instant, et personne ne peut plus les retraduire.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";
import { notificationKeys } from "@/shared/api/query-keys";

export type Notification = components["schemas"]["NotificationResponse"];
export type NotificationPage = components["schemas"]["NotificationListResponse"];
export type Preference = components["schemas"]["PreferenceResponse"];

export function useNotifications(unreadOnly = false): UseQueryResult<NotificationPage, ApiError> {
  return useQuery({
    queryKey: notificationKeys.list(unreadOnly),
    queryFn: () =>
      unwrap(api.GET("/api/v1/notifications", { params: { query: { unread_only: unreadOnly } } })),
  });
}

export function useUnreadCount(): UseQueryResult<number, ApiError> {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () =>
      (await unwrap(api.GET("/api/v1/notifications/unread-count", {}))).unread_count,
  });
}

export function useMarkRead(): UseMutationResult<Notification, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      unwrap(
        api.POST("/api/v1/notifications/{notification_id}/read", {
          params: { path: { notification_id: notificationId } },
        }),
      ),
    // Le préfixe entier : marquer une notification lue change la liste **et** le
    // badge, et n'invalider que celle qu'on a sous les yeux laisserait l'autre
    // afficher un compteur périmé — c'est-à-dire précisément le chiffre qu'on
    // regarde pour savoir s'il reste quelque chose à lire.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useMarkAllRead(): UseMutationResult<{ marked: number }, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/v1/notifications/read-all", {})),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useDeleteNotification(): UseMutationResult<unknown, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      unwrap(
        api.DELETE("/api/v1/notifications/{notification_id}", {
          params: { path: { notification_id: notificationId } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function usePreferences(): UseQueryResult<Preference[], ApiError> {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: async () => (await unwrap(api.GET("/api/v1/me/notification-preferences", {}))).items,
  });
}

export interface PreferenceChange {
  type: string;
  channel: Preference["channel"];
  enabled: boolean;
}

export function useUpdatePreferences(): UseMutationResult<
  { items: Preference[] },
  ApiError,
  PreferenceChange[]
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: PreferenceChange[]) =>
      unwrap(api.PATCH("/api/v1/me/notification-preferences", { body: { items } })),
    // La réponse porte déjà l'état complet, défauts recalculés : on l'écrit dans
    // le cache plutôt que de redemander. Une invalidation ouvrirait une fenêtre
    // pendant laquelle l'écran afficherait autre chose que ce qui vient d'être
    // enregistré, et l'interrupteur reviendrait visiblement en arrière.
    onSuccess: (data) => queryClient.setQueryData(notificationKeys.preferences(), data.items),
  });
}
